/**
 * TEND-019's attendance result for one Therapist Session.
 *
 * ⚠ THIS IS A PREVIEW, NOT THE TRUTH. `therapist_session_outcome_of` in `0100`
 * is what actually writes the column, during the nightly settle. This exists so
 * a screen can LABEL a session it has already read — History, an admin list —
 * without a round trip, exactly as the money math previews a price the Edge
 * Function recomputes.
 *
 * Both carry the same cases and both are tested. If they disagree, the label
 * lies about a stored fact, which is worse than showing nothing.
 *
 * ═══ ALL FIVE, SINCE OQ-120 ═══
 *
 * This file used to produce three. Early End is "became inactive before the
 * fixed end without an approved continuing state", and since `0098` retired
 * clock-out nothing marked a therapist inactive — she stops being REACHABLE,
 * which is a location fact, not a declaration of leaving. Picking a threshold
 * would have invented an attendance penalty nobody approved.
 *
 * OQ-120 (Leon, 2026-08-28) chose the threshold the app already has:
 * **TEND-027's five minutes**, the same window that makes her Offline on the
 * Super Admin map and, under TEND-028, excludes her from Auto-Assign. A last pin
 * older than five minutes at the fixed end means she went Offline and did not
 * come back.
 *
 * `incomplete` needs no rule of its own — TEND-019 defines it as **both** Late
 * Start and Early End.
 *
 * ⚠ THE PREVIEW NEEDS ONE MORE FACT THAN IT USED TO. `lastSeenAt` has no
 * client-side source on most screens: a therapist cannot read
 * `staff_live_locations` (granted to hub_manager and above, 0025). Pass
 * `undefined` and this returns the three-outcome answer, which is the honest
 * one for a caller that cannot see her location — never a guessed `completed`.
 */
export type TherapistSessionOutcome =
  | "completed"
  | "late_start"
  | "early_end"
  | "incomplete"
  | "no_show";

export interface TherapistSessionOutcomeInput {
  /** When she tapped Start. `null` = never started. */
  startedAt: string | null;
  /** The Session's scheduled start — 2:00 PM Manila, from the row itself. */
  startsAt: string;
  /** The Session's fixed end — midnight Manila. Needed for `early_end`. */
  endsAt?: string;
  /**
   * Her last known location fix (`staff_live_locations.captured_at`).
   *
   * ⚠ OMIT IT RATHER THAN GUESS. Most callers cannot read it — the table is
   * granted to hub_manager and above (0025) — and omitting it downgrades this to
   * the three-outcome answer instead of inventing a fifth.
   */
  lastSeenAt?: string | null;
}

/** TEND-027's five minutes — the same window that makes her Offline (OQ-120). */
const OFFLINE_WINDOW_MS = 5 * 60_000;

/**
 * ⚠ The boundary is the rule. TEND-014 has no grace period: 2:00:00 PM is on
 * time and 2:00:01 PM is a Late Start, so this is `>` and never `>=`. `0100`
 * carries the identical comparison, and `121`'s TS-002 pins the second either
 * side of it.
 */
export function therapistSessionOutcome(
  input: TherapistSessionOutcomeInput,
): TherapistSessionOutcome {
  if (input.startedAt === null) return "no_show";

  const started = Date.parse(input.startedAt);
  const scheduled = Date.parse(input.startsAt);
  // An unparseable timestamp must not silently become "completed" — that would
  // turn a data problem into a clean attendance record. No-Show is the honest
  // answer for "we cannot establish that she started".
  if (Number.isNaN(started) || Number.isNaN(scheduled)) return "no_show";

  const late = started > scheduled;

  // OQ-120. Only decidable when the caller supplied BOTH the fixed end and a
  // location fact; `endsAt` alone proves nothing, and a missing `lastSeenAt`
  // means "not visible from here", not "she was offline".
  const ends = input.endsAt === undefined ? Number.NaN : Date.parse(input.endsAt);
  // Bound to a local so the `undefined` check narrows the type here rather than
  // through a separate boolean, which TypeScript cannot follow.
  const lastSeen = input.lastSeenAt;
  if (lastSeen !== undefined && !Number.isNaN(ends)) {
    const seen = lastSeen === null ? null : Date.parse(lastSeen);
    const early = seen === null || Number.isNaN(seen) || seen < ends - OFFLINE_WINDOW_MS;
    // TEND-019: Incomplete is BOTH — checked before either alone.
    if (early && late) return "incomplete";
    if (early) return "early_end";
  }

  return late ? "late_start" : "completed";
}

/**
 * What the THERAPIST is shown — TEND-067.
 *
 * ⚠ Her own History shows the status and her exact times, and nothing else:
 * TEND-068 forbids the internal reason, the evidence, and any comparison or
 * decision trace. So this returns a label and never an explanation.
 */
export function therapistOutcomeLabel(outcome: TherapistSessionOutcome): string {
  switch (outcome) {
    case "completed":
      return "Completed";
    case "late_start":
      return "Late start";
    case "early_end":
      return "Early end";
    case "incomplete":
      return "Incomplete";
    case "no_show":
      return "No show";
  }
}
